import React, { useCallback, useEffect, useRef, useState } from 'react';
import { noop, debounce, cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { muiGlobalTheme } from '@themes';
import { colors } from '../../css';
import { searchSuggestions } from '../../css/zindex';
import { renderSuggestion } from './Suggestions';
import { WithStylesClassesPropType } from '../../../lib/commonPropTypes';

function Input({ InputProps, classes: { inputRoot, inputInput }, ref, ...other }) {
  const classes = { root: inputRoot, input: inputInput };
  const { onChange, ...otherInputProps } = InputProps;
  const props = {
    InputProps: {
      inputRef: ref,
      classes,
      ...otherInputProps,
      onChange,
      autoComplete: 'off'
    },
    ...other
  };
  return <TextField {...props} />;
}
Input.propTypes = {
  InputProps: PropTypes.shape({ onChange: PropTypes.func }).isRequired,
  classes: PropTypes.shape({ inputRoot: PropTypes.shape({}), inputInput: PropTypes.shape({}) }).isRequired,

  ref: PropTypes.shape({}).isRequired
};

const styles = (theme) => ({
  root: {
    flexGrow: 1
  },
  container: {
    flexGrow: 1,
    position: 'relative'
  },
  paper: {
    position: 'absolute',
    zIndex: searchSuggestions,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0
  },
  inputRoot: {
    flexWrap: 'wrap'
  },
  inputInput: {
    width: 'auto',
    flexGrow: 1
  }
});

const customizeMuiGlobalTheme = () => {
  const newInputTheme = cloneDeep(muiGlobalTheme);

  newInputTheme.palette.text.primary = '#000';
  newInputTheme.palette.text.secondary = colors.colorGrey03;
  newInputTheme.components.MuiIconButton = {
    styleOverrides: { root: { marginBottom: '10px' } }
  };

  return createTheme(newInputTheme);
};

function PropertyLocationAutoComplete(props) {
  const {
    onInputChange,
    debounceTime = 300,
    suggestions,
    suggestionCount,
    classes,
    theme,
    errorText,
    helperText,
    openOnFocus,
    ...defaultProps
  } = props;

  const { error, ...InputProps } = defaultProps;
  const { value, onChange, label, required, disabled, placeholder, variant } = InputProps;
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const inputTheme = useRef(customizeMuiGlobalTheme());
  const debounceEmit = useCallback(
    debounce((newValue) => onInputChange(newValue), debounceTime),
    [onInputChange, debounceTime]
  );

  const handleOnInputValueChange = (newValue, { isOpen }) => {
    if (isOpen) {
      setInputValue(newValue);
      debounceEmit(newValue);
    }
  };

  const handleOnOuterClick = () => {
    setInputValue(value);
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={inputTheme.current}>
        <div className={classes.root}>
          <Downshift
            id="downshift-simple"
            onChange={onChange}
            onInputValueChange={handleOnInputValueChange}
            itemToString={(item) => item?.label ?? ''}
            inputValue={inputValue}
            onOuterClick={handleOnOuterClick}
          >
            {({
              getInputProps,
              getItemProps,
              getMenuProps,
              openMenu,
              highlightedIndex,
              inputValue: propInputValue,
              isOpen,
              selectedItem
            }) => (
              <div className={classes.container}>
                <Input
                  variant={variant}
                  fullWidth
                  classes={classes}
                  InputProps={getInputProps(openOnFocus ? { onFocus: openMenu } : {})}
                  error={error}
                  helperText={error ? errorText : helperText}
                  label={label}
                  required={required}
                  disabled={disabled}
                  placeholder={placeholder}
                />
                <div {...getMenuProps()}>
                  {isOpen && (
                    <Paper elevation={2} className={classes.paper} square>
                      {suggestions.map((suggestion, index) =>
                        renderSuggestion({
                          suggestion,
                          index,
                          itemProps: getItemProps({ item: suggestion }),
                          highlightedIndex,
                          selectedItem,
                          inputValue: propInputValue
                        })
                      )}
                    </Paper>
                  )}
                </div>
              </div>
            )}
          </Downshift>
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

PropertyLocationAutoComplete.propTypes = {
  // Message that is displayed when the input has an error
  errorText: PropTypes.string,
  // Optional helper text that sits below the input
  helperText: PropTypes.string,
  // the suggestions for autocomplete
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })
  ),
  openOnFocus: PropTypes.bool,
  classes: WithStylesClassesPropType.isRequired,
  theme: PropTypes.shape({}).isRequired,
  onInputChange: PropTypes.func,
  suggestionCount: PropTypes.number,
  debounceTime: PropTypes.number
};

PropertyLocationAutoComplete.defaultProps = {
  errorText: 'Error',
  openOnFocus: false,
  helperText: undefined,
  suggestions: [],
  suggestionCount: 5,
  onInputChange: noop,
  debounceTime: 300
};

const StyledComponent = withStyles(styles, { withTheme: true })(PropertyLocationAutoComplete);
export default StyledComponent;
